import React, { useEffect, useState } from 'react';
import {
  Button,
  InputField,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../utils/localization';

/** What needs to be confirmed. `onConfirm` called if user agrees. */
export interface ConfirmCommand {
  title: string;
  description: string;
  captcha?: string;
  onConfirm: () => void;
}

interface Params {
  confirmRequest?: ConfirmCommand;
  clearRequest: () => void;
}

/** Generic confirm dialog. Needs title, description and success action. */
export function ConfirmOption({
  confirmRequest,
  clearRequest,
}: Params): JSX.Element {
  const l = useInvertedLocalize();
  const [isOpen, setIsOpen] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [isVerified, setIsVerified] = useState(true);

  const closeDialog = () => {
    setIsOpen(false);

    // Delay clearing the request to avoid flickering the dialog
    setTimeout(() => {
      clearRequest();
    }, 200);
  };

  const checkCaptcha = (val: string) => {
    setCaptcha(val);

    if (val === confirmRequest?.captcha) {
      setIsVerified(true);
    }
  };

  useEffect(() => {
    if (confirmRequest) {
      setIsOpen(true);
    }
  }, [confirmRequest]);

  useEffect(() => {
    setIsVerified(!confirmRequest?.captcha);
    setCaptcha('');
  }, [confirmRequest?.captcha]);

  return (
    <ModalIsland
      kind='critical'
      alertTitle={confirmRequest?.title ?? ''}
      alertSubtitle={confirmRequest?.description ?? ''}
      onClose={() => closeDialog()}
      isOpen={isOpen}
      primaryButton={
        confirmRequest?.captcha && !isVerified ? (
          <InputField
            type='text'
            name='ConfirmOption-captcha'
            placeholder=''
            value={captcha}
            setValue={checkCaptcha}
            label={
              <Typography kind='label' htmlFor='ConfirmOption-captcha'>
                {l('Type "{captcha}" to verify', {
                  captcha: confirmRequest.captcha,
                })}
              </Typography>
            }
          />
        ) : (
          <Button
            type='button'
            kind='outlined'
            size='medium'
            onClick={() => {
              confirmRequest?.onConfirm();
              closeDialog();
            }}>
            {l('Confirm')}
          </Button>
        )
      }
      secondaryButton={
        <Button
          type='button'
          kind='ghost'
          size='medium'
          onClick={() => {
            closeDialog();
          }}>
          {l('Cancel')}
        </Button>
      }
    />
  );
}
